// ============================================================================
// @jeraisonne/db — types & helpers du blog réflexif « Solve Path de la réflexion ».
// ============================================================================
// Types éditoriaux partagés par apps/app (admin) et apps/marketing (public).
// Miroir des schémas Zod de l'edge fn
// `supabase/functions/_shared/prompts/blog-article-generate.ts` — garder synchro.
// Helpers PURS (publishabilité, agrégation des sondages) testés Vitest.
// ============================================================================

import type { UserRole } from './types';

/** Type de bloc — l'ordre encode la montée en profondeur de la réflexion. */
export type BlogBlockKind =
  | 'declencheur'
  | 'premiere_analyse'
  | 'approfondissement'
  | 'contradictions'
  | 'synthese';

export interface BlogPoll {
  question: string;
  choices: string[];
}

/**
 * Un niveau de lecture d'un bloc (profondeur variable). `title` = libellé du
 * niveau (« Idée essentielle » / « Comprendre » / « Approfondir »), `content` =
 * le texte (Markdown). Chaque niveau se lit INDÉPENDAMMENT.
 */
export interface BlogLevel {
  title: string;
  content: string;
}

export interface BlogBlock {
  kind: BlogBlockKind;
  heading: string;
  /** Niveau 1 — idée essentielle (1-3 phrases, < 10 s). Affiché par défaut. */
  niveau1: BlogLevel;
  /** Niveau 2 — comprendre (1-2 paragraphes, 30-60 s). Masqué par défaut. */
  niveau2: BlogLevel;
  /** Niveau 3 — approfondir (analyse/données/penseurs, 1-3 min). Masqué. */
  niveau3: BlogLevel;
  image_prompt: string;
  /** Sondage suivant ce bloc. null/absent pour la synthèse (dernier bloc). */
  poll?: BlogPoll | null;
}

/** Libellés par défaut des 3 niveaux de lecture. */
export const BLOG_LEVEL_LABELS = {
  niveau1: 'Idée essentielle',
  niveau2: 'Comprendre',
  niveau3: 'Approfondir',
} as const;

/**
 * Longueur visée du CONTENU rédigé à la génération (verbosité des 3 niveaux),
 * indépendante de la structure (toujours 5 blocs, 3 niveaux, un sondage/bloc).
 * Miroir de `BlogArticleLength` dans
 * `supabase/functions/_shared/prompts/blog-article-generate.ts` — garder synchro.
 */
export type BlogArticleLength = 'tres_court' | 'court' | 'normal' | 'long';

/** Options ordonnées du sélecteur de longueur (admin). Défaut = `normal`. */
export const BLOG_LENGTHS: readonly { value: BlogArticleLength; label: string }[] = [
  { value: 'tres_court', label: 'Très court' },
  { value: 'court', label: 'Court' },
  { value: 'normal', label: 'Normal' },
  { value: 'long', label: 'Long' },
] as const;

/** Événement de lecture mesurable d'un niveau de bloc. */
export type BlogBlockEvent = 'level1_view' | 'level2_open' | 'level3_open';

/** Compteurs analytiques d'un bloc (profondeur de lecture atteinte). */
export interface BlogBlockMetrics {
  article_id: string;
  block_index: number;
  level1_views: number;
  level2_opens: number;
  level3_opens: number;
}

export interface BlogPenseur {
  name: string;
  position: string;
  contribution: string;
  /** Lien défini par l'admin (Wikipédia ou autre). Vide → recherche auto. */
  url?: string | null;
}

export interface BlogResource {
  title: string;
  author?: string | null;
  url?: string | null;
  why: string;
}

export interface BlogResources {
  livres: BlogResource[];
  etudes: BlogResource[];
  articles: BlogResource[];
  videos: BlogResource[];
}

export interface BlogSocial {
  linkedin: string;
  facebook: string;
  instagram_carousel: string[];
  instagram_story_poll: BlogPoll;
}

export interface BlogSeo {
  title: string;
  meta_description: string;
  slug: string;
  primary_keyword: string;
  secondary_keywords: string[];
}

export type BlogContentStatus = 'draft' | 'awaiting_review' | 'published' | 'archived';
/**
 * Tri-état de visibilité (migration 0138, calque des thèmes) :
 * `private` (brouillon de l'auteur) → `pending_review` (soumis à l'admin) →
 * `public` (approuvé, visible sur le fil /blog). La transition vers `public`
 * est réservée à l'admin (modération).
 */
export type BlogVisibility = 'private' | 'pending_review' | 'public';

/** Slide rendue du carrousel social (cache, migration 0131). */
export interface BlogCarouselSlide {
  url: string;
  kind: 'cover' | 'text' | 'poll';
}
/** Un jeu de slides rendu pour une plateforme (à son format dédié). */
export interface BlogCarouselSet {
  slides: BlogCarouselSlide[];
  generated_at: string;
}
/**
 * Cache `blog_articles.social_carousel` : un carrousel DÉDIÉ par plateforme, à
 * son format natif — Instagram en portrait 4:5, LinkedIn en carré 1:1.
 */
export interface BlogCarouselCache {
  instagram?: BlogCarouselSet | null;
  linkedin?: BlogCarouselSet | null;
}

/** État de la génération IA (asynchrone, migration 0128). */
export type BlogGenerationStatus = 'generating' | 'ready' | 'failed';

/** Ligne `blog_articles` (payload éditorial déjà désérialisé). */
export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  status: BlogContentStatus;
  visibility: BlogVisibility;
  author_id: string;
  /**
   * Audience éditoriale ciblée par le fil d'actu in-app (migration 0220).
   * Tableau vide = visible à tous les rôles. Filtre CLIENT uniquement (pas de
   * RLS) — cf. `isBlogArticleVisibleToRole`.
   */
  target_roles: UserRole[];
  illustration_url: string | null;
  illustration_prompt: string | null;
  blocks: BlogBlock[];
  penseurs: BlogPenseur[];
  pour_aller_plus_loin: BlogResources;
  social: BlogSocial;
  /** Cache des slides du carrousel social rendues (migration 0131). */
  social_carousel?: BlogCarouselCache | null;
  seo: BlogSeo;
  conclusion_md: string;
  sources_utilisees: string[];
  reading_time: number;
  view_count: number;
  generation_model: string | null;
  /** Génération asynchrone (migration 0128). */
  generation_status: BlogGenerationStatus;
  generation_error: string | null;
  generation_input: unknown | null;
  /** Génération par étapes (migration 0129). */
  generation_step: string | null;
  generation_state: unknown | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

/** Ligne `blog_poll_votes` (vote anonyme brut). */
export interface BlogPollVote {
  id: string;
  article_id: string;
  block_index: number;
  choice_index: number;
  voter_fingerprint: string | null;
  user_id: string | null;
  /** Abonné newsletter ayant voté (migration 0127). */
  subscriber_id: string | null;
  created_at: string;
}

/** Aperçu de liste (admin + marketing). */
export interface BlogArticleSummary {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  status: BlogContentStatus;
  visibility: BlogVisibility;
  illustration_url: string | null;
  reading_time: number;
  view_count: number;
  /** Audience éditoriale ciblée (migration 0220). Optionnel selon le select. */
  target_roles?: UserRole[];
  /** État de génération (présent côté admin ; absent côté marketing public). */
  generation_status?: BlogGenerationStatus;
  /** Bloc SEO — source des « tags » affichés (mots-clés). Optionnel selon le select. */
  seo?: BlogSeo;
  created_at: string;
  published_at: string | null;
}

export const BLOG_BLOCK_KIND_LABELS: Record<BlogBlockKind, string> = {
  declencheur: 'Déclencheur',
  premiere_analyse: 'Première analyse',
  approfondissement: 'Approfondissement',
  contradictions: 'Contradictions',
  synthese: 'Synthèse',
};

/** Ordre canonique des 5 blocs (profondeur croissante). */
export const BLOG_BLOCK_KIND_ORDER: readonly BlogBlockKind[] = [
  'declencheur',
  'premiere_analyse',
  'approfondissement',
  'contradictions',
  'synthese',
];

export const EMPTY_BLOG_RESOURCES: BlogResources = {
  livres: [],
  etudes: [],
  articles: [],
  videos: [],
};

export const EMPTY_BLOG_SOCIAL: BlogSocial = {
  linkedin: '',
  facebook: '',
  instagram_carousel: [],
  instagram_story_poll: { question: '', choices: [] },
};

export const EMPTY_BLOG_SEO: BlogSeo = {
  title: '',
  meta_description: '',
  slug: '',
  primary_keyword: '',
  secondary_keywords: [],
};

/**
 * Comble les sous-champs JSONB manquants d'un article. Les defaults SQL sont
 * `'{}'::jsonb` / `'[]'::jsonb` (objet/tableau vides), PAS la forme complète
 * attendue par l'éditeur admin. Un article dont la génération a échoué tôt
 * (ex. au step `plan`) a donc `seo = {}`, `social = {}`,
 * `pour_aller_plus_loin = {}` → l'éditeur crashait (`draft.seo.title.length`,
 * `pour_aller_plus_loin.livres.map`…) et le bouton « Relancer » devenait
 * inatteignable. Ce normaliseur garantit une forme stable pour que tout article
 * (échoué, partiel ou complet) s'affiche et reste relançable. Pur.
 */
export function normalizeBlogArticle(article: BlogArticle): BlogArticle {
  const seo = (article.seo ?? {}) as Partial<BlogSeo>;
  const social = (article.social ?? {}) as Partial<BlogSocial>;
  const ppl = (article.pour_aller_plus_loin ?? {}) as Partial<BlogResources>;
  return {
    ...article,
    title: article.title ?? '',
    subtitle: article.subtitle ?? '',
    conclusion_md: article.conclusion_md ?? '',
    blocks: Array.isArray(article.blocks) ? article.blocks : [],
    penseurs: Array.isArray(article.penseurs) ? article.penseurs : [],
    sources_utilisees: Array.isArray(article.sources_utilisees) ? article.sources_utilisees : [],
    pour_aller_plus_loin: {
      livres: ppl.livres ?? [],
      etudes: ppl.etudes ?? [],
      articles: ppl.articles ?? [],
      videos: ppl.videos ?? [],
    },
    social: {
      linkedin: social.linkedin ?? '',
      facebook: social.facebook ?? '',
      instagram_carousel: social.instagram_carousel ?? [],
      instagram_story_poll: social.instagram_story_poll ?? { question: '', choices: [] },
    },
    seo: {
      title: seo.title ?? '',
      meta_description: seo.meta_description ?? '',
      slug: seo.slug ?? '',
      primary_keyword: seo.primary_keyword ?? '',
      secondary_keywords: seo.secondary_keywords ?? [],
    },
  };
}

// ─── Progression de la génération multi-étapes (migration 0129) ──────────────

/** Étapes ordonnées de la génération (plan → 5 blocs → ressources → done). */
export const BLOG_GEN_STEP_ORDER = [
  'plan',
  'block_1',
  'block_2',
  'block_3',
  'block_4',
  'block_5',
  'resources',
  'done',
] as const;

export const BLOG_GEN_STEP_LABELS: Record<string, string> = {
  plan: 'Plan & sujet',
  block_1: 'Bloc 1 — Déclencheur',
  block_2: 'Bloc 2 — Première analyse',
  block_3: 'Bloc 3 — Approfondissement',
  block_4: 'Bloc 4 — Contradictions',
  block_5: 'Bloc 5 — Synthèse',
  resources: 'Ressources & réseaux',
  done: 'Terminé',
};

/**
 * Progression d'une génération à partir de `generation_step` : libellé de
 * l'étape courante, index (1-based) et total. Robuste à un step inconnu/null.
 */
export function blogGenProgress(step: string | null | undefined): {
  label: string;
  index: number;
  total: number;
  ratio: number;
} {
  // total = nombre d'étapes « travail » (hors 'done').
  const total = BLOG_GEN_STEP_ORDER.length - 1;
  const i = step ? BLOG_GEN_STEP_ORDER.indexOf(step as (typeof BLOG_GEN_STEP_ORDER)[number]) : -1;
  const index = i < 0 ? 0 : Math.min(i + 1, total);
  return {
    label: (step && BLOG_GEN_STEP_LABELS[step]) || 'Démarrage…',
    index,
    total,
    ratio: total > 0 ? index / total : 0,
  };
}

// ─── Helpers purs ─────────────────────────────────────────────────────────────

/**
 * Un sondage est valide pour le parcours si question + 2 à 4 choix non vides.
 * Le dernier bloc (synthèse) n'a PAS de sondage par design.
 */
export function isValidBlogPoll(poll: BlogPoll | null | undefined): boolean {
  if (!poll) return false;
  if (!poll.question.trim()) return false;
  const choices = poll.choices.filter((c) => c.trim().length > 0);
  return choices.length >= 2 && choices.length <= 4;
}

/**
 * Vérifie qu'un article est publiable : titre, SEO, blocs présents, chaque bloc
 * non-synthèse porte un sondage valide. Renvoie la liste des manques (vide = OK).
 * Pur — réutilisé par l'admin (garde avant publication) et testé.
 */
export function blogArticleIssues(
  article: Pick<BlogArticle, 'title' | 'blocks' | 'seo'>,
): string[] {
  const issues: string[] = [];
  const blocks = Array.isArray(article.blocks) ? article.blocks : [];
  if (!article.title?.trim()) issues.push('Titre manquant.');
  if (!article.seo?.title?.trim()) issues.push('Titre SEO manquant.');
  if (!article.seo?.meta_description?.trim()) issues.push('Méta-description SEO manquante.');
  if (blocks.length < 3) issues.push('Au moins 3 blocs sont requis.');

  blocks.forEach((block, i) => {
    if (!block.niveau1?.content.trim()) {
      issues.push(`Bloc ${i + 1} (${block.kind}) : niveau 1 (idée essentielle) vide.`);
    }
    if (!block.niveau2?.content.trim()) {
      issues.push(`Bloc ${i + 1} (${block.kind}) : niveau 2 (comprendre) vide.`);
    }
    if (!block.niveau3?.content.trim()) {
      issues.push(`Bloc ${i + 1} (${block.kind}) : niveau 3 (approfondir) vide.`);
    }
    // Chaque bloc — Y COMPRIS la synthèse — porte un sondage.
    if (!isValidBlogPoll(block.poll)) {
      issues.push(`Bloc ${i + 1} (${block.kind}) : sondage invalide (question + 2 à 4 choix).`);
    }
  });

  return issues;
}

// ─── Recherche + pagination de la liste d'articles (migration 0139) ──────────

/** Nombre d'articles par page (liste /blog marketing + /tutor/blog app). */
export const BLOG_PAGE_SIZE = 10;

// ─── Audience éditoriale du fil d'actu in-app (migration 0220) ───────────────

/** Options du sélecteur d'audience (éditeur) — `admin` exclu, pas un segment lecteur. */
export const BLOG_TARGET_ROLE_OPTIONS: readonly { value: UserRole; label: string }[] = [
  { value: 'student', label: 'Élèves' },
  { value: 'parent', label: 'Parents' },
  { value: 'tutor', label: 'Profs / tuteurs' },
  { value: 'player', label: 'Espace curieux' },
] as const;

/**
 * Un article est visible pour un rôle donné si `target_roles` est vide/absent
 * (visible à tous) OU si le rôle du lecteur y figure. Filtre ÉDITORIAL côté
 * client sur du contenu déjà public — PAS une frontière de sécurité (le slug
 * reste ouvrable directement, marketing comme app). Pur — testé.
 */
export function isBlogArticleVisibleToRole(
  targetRoles: UserRole[] | null | undefined,
  role: UserRole | null | undefined,
): boolean {
  if (!targetRoles || targetRoles.length === 0) return true;
  if (!role) return false;
  return targetRoles.includes(role);
}

/**
 * Rôles à notifier (push) lors de la publication d'un article — miroir de
 * `isBlogArticleVisibleToRole` : vide/null → tous les rôles lecteurs
 * (`admin` exclu, ce n'est pas un segment lecteur) ; sinon exactement
 * `targetRoles`. Pur — testé. Mirroré (duplication volontaire, cf. convention
 * « N miroirs » du projet) dans `supabase/functions/blog-moderate/index.ts`
 * (Deno, ne peut pas importer `@jeraisonne/db`) — garder synchro.
 */
export function blogNotificationRoles(targetRoles: UserRole[] | null | undefined): UserRole[] {
  if (!targetRoles || targetRoles.length === 0) return ['student', 'parent', 'tutor', 'player'];
  return targetRoles;
}

/**
 * « Tags » d'un article = mots-clés SEO (primary + secondary), dédupliqués
 * (insensible à la casse), non vides, plafonnés à `max`. Pur — testé.
 */
export function blogArticleTags(seo: BlogSeo | null | undefined, max = 4): string[] {
  if (!seo) return [];
  const raw = [seo.primary_keyword, ...(seo.secondary_keywords ?? [])];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const tag of raw) {
    const t = (tag ?? '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

/** Nombre de pages pour un total d'articles (≥ 1, même à 0 résultat). Pur — testé. */
export function blogPageCount(total: number, pageSize = BLOG_PAGE_SIZE): number {
  if (total <= 0 || pageSize <= 0) return 1;
  return Math.ceil(total / pageSize);
}

/** Borne un numéro de page dans [1, pageCount]. Pur — testé. */
export function clampBlogPage(page: number, pageCount: number): number {
  if (!Number.isFinite(page) || page < 1) return 1;
  const max = Math.max(1, pageCount);
  return Math.min(Math.floor(page), max);
}

/**
 * Motif `ilike` pour la recherche sur `blog_articles.search_text`
 * (`%terme%`, trimé + borné). Vide → null (pas de filtre). Pur — testé.
 */
export function blogSearchPattern(q: string | null | undefined): string | null {
  const t = (q ?? '').trim().slice(0, 100);
  if (!t) return null;
  return `%${t}%`;
}

// ─── Transitions de visibilité (tri-état, migration 0138) ────────────────────

/** Un brouillon `private` peut être soumis à la validation admin. Pur — testé. */
export function canSubmitBlogForReview(visibility: BlogVisibility): boolean {
  return visibility === 'private';
}

/** Un article `pending_review` peut être retiré (retour `private`). Pur — testé. */
export function canWithdrawBlogReview(visibility: BlogVisibility): boolean {
  return visibility === 'pending_review';
}

/**
 * Libellé FR de l'état de publication d'un article (barre d'action + liste).
 * Combine visibilité (tri-état) et status éditorial. Pur — testé.
 */
export function blogStatusLabel(visibility: BlogVisibility, status: BlogContentStatus): string {
  if (visibility === 'public' && status === 'published') return 'Publié';
  if (visibility === 'pending_review') return 'En attente de validation';
  if (status === 'archived') return 'Archivé';
  return 'Brouillon';
}

/**
 * Index du sondage « gâté » ACTIF (la « frontière » de lecture) : le premier
 * bloc déjà révélé, porteur d'un sondage, dont le sondage n'a pas encore été
 * répondu/passé. C'est exactement là où en est le lecteur — donc visible — et
 * c'est le seul endroit où le parcours affiche l'encart d'inscription newsletter
 * (réservé aux non-abonnés). Comme la frontière suit le lecteur quand il « passe »
 * un sondage, l'encart n'est JAMAIS empilé : un seul à l'écran, toujours visible.
 *
 * `revealed[i]` = bloc dévoilé ; `acted[i]` = sondage voté OU passé. Renvoie -1
 * s'il n'y a pas de sondage actif. Pur — testé, consommé par `ReflectionJourney`.
 */
export function activeGatedPollIndex(
  blocks: Pick<BlogBlock, 'poll'>[],
  revealed: boolean[],
  acted: boolean[],
): number {
  // TOUS les blocs portent un sondage, y compris la synthèse (le dernier).
  for (let i = 0; i < blocks.length; i++) {
    if (revealed[i] && blocks[i]?.poll && !acted[i]) return i;
  }
  return -1;
}

export interface PollDistribution {
  /** Nombre de votes par index de choix (aligné sur l'ordre des choix). */
  counts: number[];
  /** Pourcentage entier par choix (somme ~100, arrondi). */
  percentages: number[];
  total: number;
}

/**
 * Agrège des votes bruts (RPC `record_blog_poll_vote` → [{choice_index,count}])
 * en distribution alignée sur `choiceCount` choix. Pur, robuste aux trous.
 */
export function pollDistribution(
  rawCounts: Array<{ choice_index: number; count: number }> | null | undefined,
  choiceCount: number,
): PollDistribution {
  const counts = new Array<number>(Math.max(0, choiceCount)).fill(0);
  for (const row of rawCounts ?? []) {
    if (row.choice_index >= 0 && row.choice_index < counts.length) {
      counts[row.choice_index] = row.count;
    }
  }
  const total = counts.reduce((s, c) => s + c, 0);
  const percentages = counts.map((c) => (total === 0 ? 0 : Math.round((c / total) * 100)));
  return { counts, percentages, total };
}

// ─── Statistiques admin des sondages d'un article (page de consultation) ─────

/**
 * Vote brut nécessaire à l'agrégation des stats — sous-ensemble de
 * `blog_poll_votes`. `subscriber_id` est null pour les votes anonymes legacy
 * (avant la migration 0127 qui a réservé le vote aux abonnés confirmés).
 */
export interface BlogPollVoteRecord {
  block_index: number;
  choice_index: number;
  subscriber_id: string | null;
}

/** Distribution d'un sondage (un bloc) + son index de bloc. */
export interface BlogPollBlockStats {
  blockIndex: number;
  distribution: PollDistribution;
}

/** Stats agrégées de tous les sondages d'un article (vue admin/consultation). */
export interface ArticlePollStats {
  /** Une entrée par bloc porteur d'un sondage, dans l'ordre croissant des blocs. */
  perBlock: BlogPollBlockStats[];
  /** Nombre total de votes, tous sondages confondus. */
  totalVotes: number;
  /** Nombre d'abonnés DISTINCTS ayant répondu à au moins un sondage. */
  respondentCount: number;
}

/**
 * Agrège des votes bruts en statistiques par sondage pour la page de
 * consultation admin. `choiceCountByBlock[i]` = nombre de choix du sondage du
 * bloc i (0 / absent = bloc sans sondage, ignoré). `respondentCount` compte les
 * abonnés DISTINCTS (`subscriber_id` non-null). Pur — testé.
 */
export function aggregateArticlePollStats(
  votes: BlogPollVoteRecord[],
  choiceCountByBlock: number[],
): ArticlePollStats {
  const perBlock: BlogPollBlockStats[] = [];
  let totalVotes = 0;
  for (let i = 0; i < choiceCountByBlock.length; i++) {
    const choiceCount = choiceCountByBlock[i] ?? 0;
    if (choiceCount <= 0) continue;
    const grouped = new Map<number, number>();
    for (const v of votes) {
      if (v.block_index !== i) continue;
      grouped.set(v.choice_index, (grouped.get(v.choice_index) ?? 0) + 1);
    }
    const rawCounts = Array.from(grouped, ([choice_index, count]) => ({ choice_index, count }));
    const distribution = pollDistribution(rawCounts, choiceCount);
    perBlock.push({ blockIndex: i, distribution });
    totalVotes += distribution.total;
  }
  const respondents = new Set<string>();
  for (const v of votes) if (v.subscriber_id) respondents.add(v.subscriber_id);
  return { perBlock, totalVotes, respondentCount: respondents.size };
}

/** Réponse d'un abonné à un sondage (un bloc). */
export interface BlogPollRespondentVote {
  blockIndex: number;
  choiceIndex: number;
}

/**
 * Regroupe des votes (joints à leur abonné) par abonné DISTINCT, avec la liste
 * de ses réponses triées par bloc. Ignore les votes sans abonné. Trie les
 * abonnés par nombre de réponses décroissant puis ordre d'apparition (stable).
 * Générique sur la forme de l'abonné (au moins un `id`). Pur — testé. Alimente
 * la page newsletter filtrée sur les répondants d'un article.
 */
export function collectPollRespondents<S extends { id: string }>(
  rows: Array<{ block_index: number; choice_index: number; subscriber: S | null }>,
): Array<{ subscriber: S; answers: BlogPollRespondentVote[] }> {
  const byId = new Map<
    string,
    { subscriber: S; answers: BlogPollRespondentVote[]; order: number }
  >();
  let order = 0;
  for (const row of rows) {
    const sub = row.subscriber;
    if (!sub) continue;
    let entry = byId.get(sub.id);
    if (!entry) {
      entry = { subscriber: sub, answers: [], order: order++ };
      byId.set(sub.id, entry);
    }
    // 1 vote/abonné/sondage en base ; on dé-duplique par robustesse.
    if (!entry.answers.some((a) => a.blockIndex === row.block_index)) {
      entry.answers.push({ blockIndex: row.block_index, choiceIndex: row.choice_index });
    }
  }
  return Array.from(byId.values())
    .map((e) => {
      e.answers.sort((a, b) => a.blockIndex - b.blockIndex);
      return e;
    })
    .sort((a, b) => b.answers.length - a.answers.length || a.order - b.order)
    .map(({ subscriber, answers }) => ({ subscriber, answers }));
}

// ─── Publications réseaux sociaux associées à l'article ──────────────────────

/** Réseau social adressable d'un article (icône + lien dans le blog public). */
export type BlogSocialNetwork = 'linkedin' | 'facebook' | 'instagram';

/**
 * Réseaux pour lesquels l'article a une publication associée — c.-à-d. dont le
 * contenu généré (`social.linkedin`/`facebook`/`instagram_carousel`) est non
 * vide. Sert à n'afficher l'icône du réseau (lien vers le compte officiel) que
 * lorsqu'un post existe pour ce réseau. Pur — testé, ordre stable LinkedIn →
 * Facebook → Instagram.
 */
export function articleSocialNetworks(social: BlogSocial | null | undefined): BlogSocialNetwork[] {
  if (!social) return [];
  const out: BlogSocialNetwork[] = [];
  if (social.linkedin?.trim()) out.push('linkedin');
  if (social.facebook?.trim()) out.push('facebook');
  if (
    Array.isArray(social.instagram_carousel) &&
    social.instagram_carousel.some((s) => s?.trim())
  ) {
    out.push('instagram');
  }
  return out;
}

/** URL publique du POST de l'article par réseau (publications réellement faites). */
export type BlogSocialPostLinks = Partial<Record<BlogSocialNetwork, string | null>>;

/** Un réseau à présenter sous l'article, avec l'URL de son post réel s'il existe. */
export interface BlogSocialLink {
  network: BlogSocialNetwork;
  /** URL publique du post publié sur ce réseau, ou `null` (→ compte officiel). */
  postUrl: string | null;
}

/**
 * Réseaux à présenter (« Réagissez sur les réseaux ») avec, pour chacun, l'URL
 * du POST réellement publié quand elle existe (`postLinks`) — sinon `null` (le
 * front retombe alors sur le compte officiel JeRaisonne). Un réseau apparaît
 * dès qu'il a du contenu généré OU une publication réelle. Ordre stable
 * LinkedIn → Facebook → Instagram. Pur — testé.
 */
export function articleSocialLinks(
  social: BlogSocial | null | undefined,
  postLinks?: BlogSocialPostLinks,
): BlogSocialLink[] {
  const generated = new Set(articleSocialNetworks(social));
  const out: BlogSocialLink[] = [];
  for (const network of ['linkedin', 'facebook', 'instagram'] as const) {
    const postUrl = postLinks?.[network]?.trim() || null;
    if (generated.has(network) || postUrl) out.push({ network, postUrl });
  }
  return out;
}

// ─── Liens externes des références (penseurs, ressources) ────────────────────

function isHttpUrl(u: string | null | undefined): boolean {
  if (!u) return false;
  try {
    const { protocol } = new URL(u.trim());
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Lien Wikipédia ROBUSTE pour un penseur. On vise `Special:Search?go=Go` : si le
 * terme correspond exactement à un article, Wikipédia y redirige directement ;
 * sinon il affiche les résultats de recherche → jamais de lien mort (on ne
 * devine pas le titre exact de l'article, souvent différent du nom saisi).
 */
export function wikipediaSearchUrl(name: string, lang = 'fr'): string {
  const q = encodeURIComponent(name.trim());
  return `https://${lang}.wikipedia.org/wiki/Special:Search?go=Go&search=${q}`;
}

/** Recherche web générique (fallback quand une ressource n'a pas d'URL fiable). */
export function webSearchUrl(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query.trim())}`;
}

/** Recherche YouTube (fallback pour une vidéo sans URL). */
export function youtubeSearchUrl(query: string): string {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(query.trim())}`;
}

/**
 * Lien externe d'un penseur : l'URL définie par l'admin si elle est http(s)
 * valide, sinon une recherche Wikipédia par nom (cf. `wikipediaSearchUrl`).
 * L'admin peut ainsi corriger un lien quand la recherche ne tombe pas juste.
 */
export function blogPenseurHref(penseur: Pick<BlogPenseur, 'name' | 'url'>, lang = 'fr'): string {
  if (isHttpUrl(penseur.url)) return penseur.url!.trim();
  return wikipediaSearchUrl(penseur.name, lang);
}

export type BlogResourceKind = 'livres' | 'etudes' | 'articles' | 'videos';

/**
 * Lien externe TOUJOURS cliquable d'une ressource « pour aller plus loin » :
 * - l'URL fournie si elle est valide (http/https) — la génération ne la pose
 *   que lorsqu'elle est sûre (« pas d'URL inventée », cf. prompt) ;
 * - sinon une recherche construite depuis le titre + l'auteur (YouTube pour une
 *   vidéo, recherche web sinon). Évite tout lien mort sans halluciner d'URL.
 */
export function blogResourceHref(
  resource: Pick<BlogResource, 'title' | 'author' | 'url'>,
  kind?: BlogResourceKind,
): string {
  if (isHttpUrl(resource.url)) return resource.url!.trim();
  const query = [resource.title, resource.author].filter(Boolean).join(' ').trim();
  return kind === 'videos' ? youtubeSearchUrl(query) : webSearchUrl(query);
}

// ─── État local du parcours (réponses + progression de lecture) ──────────────

/**
 * État du parcours de réflexion mémorisé côté navigateur (localStorage) afin de
 * le RESTAURER À L'IDENTIQUE au rafraîchissement de la page :
 * - `votes` : les réponses ENREGISTRÉES aux sondages (index de bloc → index de choix) ;
 * - `skipped` : la progression « lu sans voter » (blocs dévoilés sans réponse) ;
 * - `pending` : les réponses CHOISIES sans être abonné — le résultat du sondage
 *   reste verrouillé derrière l'encart d'inscription tant que le lecteur n'a pas
 *   confirmé son email. On les mémorise pour ne pas perdre « tu as répondu X »
 *   au rafraîchissement (et les rejouer automatiquement une fois abonné).
 *
 * Sans `skipped`, un rafraîchissement re-verrouillait le parcours au premier
 * sondage non répondu — le lecteur perdait sa place ET l'impression que ses
 * réponses avaient « disparu ».
 */
export interface BlogJourneyState {
  votes: Record<number, number>;
  skipped: Record<number, boolean>;
  pending: Record<number, number>;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function toNumberMap(v: unknown): Record<number, number> {
  const out: Record<number, number> = {};
  if (!isPlainRecord(v)) return out;
  for (const [k, val] of Object.entries(v)) {
    const ki = Number(k);
    if (Number.isInteger(ki) && typeof val === 'number' && Number.isFinite(val)) out[ki] = val;
  }
  return out;
}

function toBoolMap(v: unknown): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  if (!isPlainRecord(v)) return out;
  for (const [k, val] of Object.entries(v)) {
    const ki = Number(k);
    if (Number.isInteger(ki) && val === true) out[ki] = true;
  }
  return out;
}

/**
 * Parse l'état du parcours depuis le brut localStorage. Toujours robuste : tout
 * champ absent / illisible → vide (jamais d'exception). Rétro-compatible avec
 * l'ANCIEN format (une map de votes seule, sans enveloppe `{ votes, skipped }`).
 */
export function parseBlogJourneyState(raw: string | null | undefined): BlogJourneyState {
  if (!raw) return { votes: {}, skipped: {}, pending: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { votes: {}, skipped: {}, pending: {} };
  }
  if (!isPlainRecord(parsed)) return { votes: {}, skipped: {}, pending: {} };
  // Nouveau format = { votes, skipped, pending }. Ancien format = map de votes à
  // la racine (clés = index de bloc, jamais « votes »/« skipped »/« pending »).
  if (
    isPlainRecord(parsed.votes) ||
    isPlainRecord(parsed.skipped) ||
    isPlainRecord(parsed.pending)
  ) {
    return {
      votes: toNumberMap(parsed.votes),
      skipped: toBoolMap(parsed.skipped),
      pending: toNumberMap(parsed.pending),
    };
  }
  return { votes: toNumberMap(parsed), skipped: {}, pending: {} };
}

/** Sérialise l'état du parcours pour localStorage. */
export function serializeBlogJourneyState(state: BlogJourneyState): string {
  return JSON.stringify({ votes: state.votes, skipped: state.skipped, pending: state.pending });
}

// ============================================================================
// Raffinage manuel itératif : évaluation « juge » + versions par bloc (0135)
// ----------------------------------------------------------------------------
// Après la génération, l'admin fait NOTER l'article (5 axes /10, 3 niveaux :
// global / bloc / sondage), régénère manuellement les parties sous le seuil et
// réévalue jusqu'à « article parfait ». Chaque régénération conserve une VERSION
// du bloc (avec ses notes). Types miroir des schémas Zod de l'edge fn
// `_shared/prompts/blog-article-evaluate.ts` — garder synchro (clés ASCII).
// ============================================================================

/** Les 5 axes d'évaluation (clés ASCII — JSON stable, pas d'accents en clé). */
export const BLOG_EVAL_AXES = [
  'accessibilite',
  'rythme',
  'interet',
  'pertinence',
  'cible',
] as const;
export type BlogEvalAxis = (typeof BLOG_EVAL_AXES)[number];

/** Libellés FR (accentués) des axes pour l'affichage. */
export const BLOG_EVAL_AXIS_LABELS: Record<BlogEvalAxis, string> = {
  accessibilite: 'accessibilité',
  rythme: 'rythme',
  interet: 'intérêt',
  pertinence: 'pertinence',
  cible: 'cible',
};

/** Seuil de note par défaut (sur 10) — modifiable dans l'UI. */
export const DEFAULT_BLOG_EVAL_THRESHOLD = 7;
/** Plafond d'itérations conseillé (au-delà : avertissement, pas de blocage). */
export const MAX_BLOG_EVAL_ITERATIONS = 5;

/** Notes /10 sur les 5 axes (niveau global, bloc ou sondage). */
export interface BlogAxisScores {
  accessibilite: number;
  rythme: number;
  interet: number;
  pertinence: number;
  cible: number;
}

/** Évaluation d'un sondage (les mêmes axes que le bloc + critique). */
export interface BlogPollEvaluation {
  scores: BlogAxisScores;
  critique: string;
  axes_progression: string[];
}

/** Évaluation d'un bloc (+ celle de son sondage, si présent). */
export interface BlogBlockEvaluation {
  block_index: number;
  scores: BlogAxisScores;
  critique: string;
  axes_progression: string[];
  poll?: BlogPollEvaluation | null;
}

/** Ligne `blog_article_evaluations` (une itération d'évaluation). */
export interface BlogEvaluation {
  id: string;
  article_id: string;
  iteration: number;
  threshold: number;
  global_scores: BlogAxisScores;
  global_critique: string;
  global_axes_progression: string[];
  blocks: BlogBlockEvaluation[];
  overall_score: number | null;
  is_perfect: boolean;
  model: string | null;
  created_at: string;
}

/** Origine d'une version de bloc. */
export type BlogBlockVersionSource = 'generation' | 'refine_ai' | 'manual';

/** Ligne `blog_block_versions` (une version conservée d'un bloc + ses notes). */
export interface BlogBlockVersion {
  id: string;
  article_id: string;
  block_index: number;
  version_no: number;
  label: string;
  source: BlogBlockVersionSource;
  /** Snapshot complet du bloc (switch = remplacement de `blocks[block_index]`). */
  block: BlogBlock;
  scores: BlogAxisScores | null;
  critique: string;
  axes_progression: string[];
  poll_scores: BlogAxisScores | null;
  poll_critique: string;
  /** Remarques manuelles ayant servi à produire cette version (traçabilité). */
  manual_remarks: string;
  is_active: boolean;
  evaluated_at: string | null;
  created_at: string;
}

/** Moyenne des 5 axes (sur 10). */
export function axisAverage(scores: BlogAxisScores): number {
  const vals = BLOG_EVAL_AXES.map((a) => scores[a]);
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/** Vrai si CHAQUE axe est ≥ seuil (un score absent/non numérique ⇒ faux). */
export function scoresAboveThreshold(
  scores: BlogAxisScores | null | undefined,
  threshold: number,
): boolean {
  if (!scores) return false;
  return BLOG_EVAL_AXES.every((a) => typeof scores[a] === 'number' && scores[a] >= threshold);
}

/**
 * Indices des blocs sous le seuil : le bloc OU son sondage a au moins un axe
 * sous le seuil. Ce sont les seuls blocs touchés par « régénérer tout ».
 */
export function blocksBelowThreshold(evaluation: BlogEvaluation, threshold: number): number[] {
  return (evaluation.blocks ?? [])
    .filter(
      (b) =>
        !scoresAboveThreshold(b.scores, threshold) ||
        (b.poll != null && !scoresAboveThreshold(b.poll.scores, threshold)),
    )
    .map((b) => b.block_index);
}

/** « Article parfait » : global + chaque bloc + chaque sondage tous ≥ seuil. */
export function isArticlePerfect(evaluation: BlogEvaluation, threshold: number): boolean {
  if (!scoresAboveThreshold(evaluation.global_scores, threshold)) return false;
  return (evaluation.blocks ?? []).every(
    (b) =>
      scoresAboveThreshold(b.scores, threshold) &&
      (b.poll == null || scoresAboveThreshold(b.poll.scores, threshold)),
  );
}

// ============================================================================
// Badge « N raisonneurs » (comptes distincts ayant voté) — promu depuis
// apps/marketing/src/lib/blog-readers.ts (migration 0134 + audience in-app
// 0220) pour être partagé par apps/marketing ET apps/app. `blog-readers.ts`
// n'est plus qu'un ré-export de ces mêmes fonctions.
// ============================================================================

/** Libellé « N raisonneur·s » (chaîne vide si ≤ 0 — on masque alors le badge). */
export function activeReadersLabel(n: number): string {
  if (n <= 0) return '';
  return `${n} raisonneur${n > 1 ? 's' : ''}`;
}

/** Vote agrégé renvoyé par les RPC de distribution (block_index, choice_index, count). */
export interface BlogPollCount {
  block_index: number;
  choice_index: number;
  count: number;
}

/**
 * Regroupe une distribution plate (lignes block_index/choice_index/count) par
 * index de bloc — forme attendue par les barres de sondage. Pur et client-safe
 * (aucun import Supabase) : partagé entre le fetch SSR marketing, la
 * réconciliation en direct du parcours marketing, et le lecteur in-app.
 * Les lignes sans `block_index` numérique sont ignorées (robustesse).
 */
export function groupBlogPollCounts(
  rows: BlogPollCount[] | null | undefined,
): Record<number, BlogPollCount[]> {
  const grouped: Record<number, BlogPollCount[]> = {};
  for (const row of rows ?? []) {
    if (typeof row?.block_index !== 'number') continue;
    (grouped[row.block_index] ??= []).push(row);
  }
  return grouped;
}

/**
 * Émis après un vote ENREGISTRÉ → le badge « raisonneurs » se recompte en
 * direct. Juste le nom de l'event : le dispatch (`window.dispatchEvent`)
 * reste côté app — ce package n'a pas la lib DOM dans son tsconfig (partagé
 * avec les edge fns Deno), `emitBlogVoteRecorded` est donc défini localement
 * dans chaque app (apps/marketing/src/lib/blog-readers.ts,
 * apps/app/src/lib/blog/blog-client.ts).
 */
export const BLOG_VOTE_EVENT = 'blog:vote-recorded';

/** Score formaté pour un badge (entier tel quel, sinon une décimale). */
function formatScore(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** Texte inline d'un badge de notes (« accessibilité 7 · rythme 8 · … »). */
export function formatAxisScores(scores: BlogAxisScores | null | undefined): string {
  if (!scores) return 'non évalué';
  return BLOG_EVAL_AXES.map((a) => `${BLOG_EVAL_AXIS_LABELS[a]} ${formatScore(scores[a])}`).join(
    ' · ',
  );
}
